import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5058').replace(/\/$/, '');

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  window.dispatchEvent(new CustomEvent('mmc-access-token-changed', { detail: token }));
};
export const getAccessToken = () => accessToken;

const clearPersistedAuth = () => {
  localStorage.removeItem('mmc-auth');
  localStorage.removeItem('selectedOrgId');
  localStorage.removeItem('selectedSubOrgId');
};

const refreshAccessToken = async (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/auth/refresh-token`, {}, { withCredentials: true })
      .then((response) => response.data.data.accessToken as string)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    const orgId = localStorage.getItem('selectedOrgId');
    const subOrgId = localStorage.getItem('selectedSubOrgId');
    if (orgId) config.headers['X-Organisation-Id'] = orgId;
    if (subOrgId) config.headers['X-SubOrganisation-Id'] = subOrgId;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/register') ||
      requestUrl.includes('/api/auth/refresh-token') ||
      requestUrl.includes('/api/auth/forgot-password') ||
      requestUrl.includes('/api/auth/reset-password');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        setAccessToken(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch {
        setAccessToken(null);
        clearPersistedAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
